// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the one side panel of a headset session (the AR refresh,
 * 2026-10-04). The dock's Browse, Layers, Data, View and Help each open
 * it on their own page; only one is open at a time, in one place to the
 * right of the globe, so panels no longer pile up there.
 *
 * What each page shows comes from the host (vrSession → main.ts) through
 * `VrSidePanelHost`, polled a few times a second while the panel is open
 * and redrawn only when something it draws changed. Presses come back as
 * `VrPanelAction`s: what is the panel's own business (a tab, a filter, a
 * page, a key of the search keyboard) is handled here, the rest goes to
 * the host. The pages themselves are drawn by `vrBrowse` (Browse) and
 * `vrPanels` (Layers, View, Data, Help).
 */

import type * as THREE from 'three'
import type { VrDatasetEntry } from './vrSession'
import type { VrPanelKind } from './vrHud'
import type { ChartGeometry } from './measure/chartGeometry'
import type { SurfaceMode } from './measure/surfaceMask'
import type { VrLegend } from './vrLegend'
import {
  clampScroll,
  createCanvasPanel,
  insideRegion,
  onScrollbar,
  scrollbarThumb,
  scrollForThumb,
  type ScrollRegion,
} from './vrPanelKit'
import { browseResults, drawBrowse, initialBrowseView, reduceBrowse, type VrBrowseView } from './vrBrowse'
import { LAYER_ROW_H, drawData, drawHelp, drawLayers, drawView, initialLayersView, reducePinEdit, type VrLayersView, type VrPinEdit } from './vrPanels'
import type { RtPlayingEntry } from './overlayDisplay'
import type { DataKind } from './datasetTaxonomy'
import { referenceLinesVisible, setReferenceLinesVisible } from './referenceLines'

/** World size of the side panel; 1 canvas pixel ≈ 0.7 mm. */
export const SIDE_PANEL_WIDTH = 0.784
export const SIDE_PANEL_HEIGHT = 0.7
export const SIDE_CANVAS_W = 1120
export const SIDE_CANVAS_H = 1000

export type MapLayerTintChoice = 'white' | 'black' | 'source'

/** The Layers page, as the 2D Tools → Layers tab holds it. */
/** A real-time stream the headset's list offers; AYNI (owner, 2026-10-20): with its kind's word (Live, Forecast…) for its pill. */
export interface VrRtChoice {
  id: string
  title: string
  kind?: string
  kindId?: DataKind
}

/** AYNI (satellites plan): the Satellites section of the Layers page. */
export interface VrSatellitesState {
  on: boolean
  /** The globe shows the Earth; another body holds the switch and draws none (owner, 2026-10-23). */
  onEarth: boolean
  /** Show sets on ('highlights', 'all', 'starlink', the debris groups). */
  show: readonly string[]
  colourBy: 'orbit' | 'country' | 'purpose'
  /** Country waits for the satellite catalogue. */
  countryReady: boolean
  altitude: 'true' | 'compact'
  trails: boolean
  live: boolean
  speed: number
  /** "949 shown · orbits as of …", or loading / error. */
  status: string
  /** A second line under the switches (the sky's: the ISS's next pass). */
  detail?: string
  stale: boolean
  /** Sky above me: on, and whether this session can show it (passthrough AR). */
  sky: boolean
  skyAvailable: boolean
  /** Fully VR: the night sky drawn over the room (only while the sky is on). */
  fullVr: boolean
  /** Sky above me: the catalog's satellites passing over the viewer, each with a dataset to load (step 3 of the catalog plan). */
  passes?: ReadonlyArray<{ text: string; datasetId: string | null }>
}

export interface VrLayersState {
  overlays: ReadonlyArray<{ id: string; title: string }>
  /** AYNI (2026-10-09): the overlays on (up to `overlayCap`), first added first, each in its colour. */
  overlaysOn: ReadonlyArray<{ id: string; title: string; tint: MapLayerTintChoice }>
  overlayCap: number
  /** Real-time streams that may play over the dataset. */
  rt: ReadonlyArray<VrRtChoice>
  rtSelected: readonly string[]
  rtCap: number
  /** AYNI: the streams playing now, bottom first, whether each is hidden with its eye, and its colours. */
  rtPlaying: ReadonlyArray<RtPlayingEntry>
  /** AYNI (owner, 2026-10-12): the dataset under them, with its eye; null without one. */
  base?: { title: string; hidden: boolean; /** Its ramp, low first (owner, 2026-10-13: its palette button opens View); absent when its colours cannot change. */ ramp?: readonly string[] } | null
  /** AYNI (satellites plan): absent where the page has no Satellites layer. */
  satellites?: VrSatellitesState | null
}

/** The View page: the dataset's colours. */
export interface VrViewState {
  /** The dataset carries values, so its colours can change. */
  hasScale: boolean
  palettes: ReadonlyArray<{ id: string; label: string; stops: readonly string[] }>
  palette: string
  inverted: boolean
  /** Nothing to reset. */
  isDefault: boolean
  /** AYNI (owner, 2026-10-11): the legend, shown here while it is parked rather than beside the globe. */
  legend?: VrLegend | null
  legendParked?: boolean
  /** AYNI (owner, 2026-10-12): the dataset's opacity, 1 opaque … 0 transparent; absent without a dataset. */
  opacity?: number
  /** AYNI (owner, 2026-10-14): the reference lines' switch, on or off; null when the dataset has none. */
  references?: boolean | null
}

/** The Data page: pins and their graph. */
export interface VrPinsState {
  /** Pins can be read on this dataset. */
  readable: boolean
  /** Why not, when not: nothing loaded, a picture, or the plan. */
  reason: 'none' | 'picture' | 'plan' | null
  picking: boolean
  full: boolean
  /** The most pins the plan allows now. */
  max: number
  /**
   * `name` as shown; AYNI (owner, 2026-10-09): `own` is the pin's own
   * name ("Pin 2"), and `named` says the viewer gave it `name`.
   */
  pins: ReadonlyArray<{ id: string; color: string; name: string; own: string; named: boolean; value: string; where: string }>
  /** The graph, when two or more frames have been read. */
  chart: (ChartGeometry & {
    minLabel: string
    maxLabel: string
    startLabel: string
    endLabel: string
    /** Where playback is, 0..1 along the axis, or null. */
    now: number | null
    /** AYNI: the reference lines' short names, in the order of `references`. */
    referenceLabels?: readonly string[]
  }) | null
  /**
   * AYNI (owner, 2026-10-18): with the highest and lowest marked, whether
   * they follow the data and show their track; null (or absent) with none,
   * or on classes.
   */
  follow?: { on: boolean; track: boolean } | null
  /** AYNI (owner, 2026-10-18): where the extremes are looked for; null (or absent) while the land and sea tiles are not there. */
  surface?: SurfaceMode | null
}

export type VrPanelAction =
  | { kind: 'close' }
  | { kind: 'browse-tab'; tab: 'catalog' | 'recent' }
  | { kind: 'browse-kind'; value: string | null }
  | { kind: 'browse-theme'; value: string | null }
  | { kind: 'browse-select'; id: string; locked: string | null }
  | { kind: 'browse-search' }
  | { kind: 'key'; key: string }
  | { kind: 'layer-section'; section: 'overlay' | 'rt' | 'satellites' }
  /** AYNI (satellites plan): the Satellites section's switches. */
  | { kind: 'sat'; op: 'on' | 'trails' | 'live' | 'sky' | 'north' | 'fullVr' | 'debris' }
  | { kind: 'sat-show'; set: 'highlights' | 'all' | 'starlink' | 'with-datasets' }
  | { kind: 'sat-colour'; by: 'orbit' | 'country' | 'purpose' }
  | { kind: 'sat-altitude'; scale: 'true' | 'compact' }
  | { kind: 'sat-speed'; speed: number }
  | { kind: 'layer-overlay'; id: string | null }
  | { kind: 'layer-tint'; id: string; tint: MapLayerTintChoice }
  | { kind: 'layer-rt'; id: string | null }
  | { kind: 'layer-rt-hidden'; id: string; hidden: boolean }
  /** AYNI (2026-10-09): a press on a stream's grip (a drag from it moves the stream). */
  | { kind: 'layer-rt-grip'; id: string }
  /** Open a stream's colours, or go back to the list with null. */
  | { kind: 'layer-rt-colours'; id: string | null }
  | { kind: 'rt-palette'; id: string; palette: string }
  | { kind: 'rt-limit'; id: string; end: 'min' | 'max'; dir: -1 | 1 }
  /** AYNI (owner, 2026-10-20): the stream's Invert checkbox; `on` is the state it asks for. */
  | { kind: 'rt-invert'; id: string; on: boolean }
  /** AYNI (owner, 2026-10-12): a step of a stream's transparency (1 more transparent). */
  | { kind: 'rt-opacity'; id: string; dir: -1 | 1 }
  /** AYNI (owner, 2026-10-12): the dataset's eye, its ✕ (it and its overlays off), and a step of its transparency. */
  | { kind: 'base-hidden'; hidden: boolean }
  | { kind: 'base-close' }
  /** AYNI (owner, 2026-10-13): the dataset's palette button: its colours and transparency, on the View page. */
  | { kind: 'base-colours' }
  | { kind: 'base-opacity'; dir: -1 | 1 }
  | { kind: 'palette'; id: string }
  | { kind: 'invert' }
  /** AYNI (owner, 2026-10-14): show or hide the reference lines. */
  | { kind: 'references' }
  | { kind: 'reset-view' }
  /** AYNI: park the legend on the View page, or put it back beside the globe. */
  | { kind: 'legend-park'; parked: boolean }
  | { kind: 'pin-remove'; id: string }
  /** AYNI: rename a pin: open its keyboard, then the keys typed on it. */
  | { kind: 'pin-rename'; id: string }
  | { kind: 'pin-key'; key: string }
  | { kind: 'pins-clear' }
  | { kind: 'pins-extremes' }
  | { kind: 'pins-follow' }
  | { kind: 'pins-track' }
  | { kind: 'pins-surface'; mode: SurfaceMode }

export type VrSatelliteAction = Extract<VrPanelAction, { kind: 'sat' | 'sat-show' | 'sat-colour' | 'sat-altitude' | 'sat-speed' }>

/** Two presses are the same press when every field matches (press-and-release semantics). */
export function panelActionsEqual(a: VrPanelAction, b: VrPanelAction): boolean {
  if (a.kind !== b.kind) return false
  const ra = a as unknown as Record<string, unknown>
  const rb = b as unknown as Record<string, unknown>
  for (const k of Object.keys(ra)) if (ra[k] !== rb[k]) return false
  return Object.keys(ra).length === Object.keys(rb).length
}

export interface VrSidePanelHost {
  /** The catalogue (polled once a second while Browse is open). */
  datasets(): VrDatasetEntry[]
  /** Recently opened datasets, newest first. */
  recentIds(): string[]
  /** Dataset ids ranked for a query, or null to fall back on titles. */
  search?(query: string): string[] | null
  /** The id on the globe, to mark its card. */
  currentId(): string | null
  layers(): VrLayersState | null
  /** AYNI (satellites plan): a press in the Satellites section. */
  satellite?(action: VrSatelliteAction): void
  view(): VrViewState | null
  pins(): VrPinsState | null
  loadDataset(id: string): void
  /** A stream outside the plan was chosen: say so instead of loading it. */
  lockedChosen(packageName: string, title: string): void
  setOverlay(id: string | null): void
  /** The colour of the overlay `id`. */
  setTint(id: string, tint: MapLayerTintChoice): void
  toggleRt(id: string | null): void
  /** AYNI: hide a playing stream on the globe, or show it again (its eye). */
  setRtHidden(id: string, hidden: boolean): void
  /** AYNI (2026-10-09): move a playing stream to `to` in the stack (0 is the lowest). */
  moveRt(id: string, to: number): void
  setRtPalette(id: string, palette: string): void
  /** One step of a stream's lower or upper colour limit. */
  stepRtLimit(id: string, end: 'min' | 'max', dir: -1 | 1): void
  /** AYNI (owner, 2026-10-20): run a stream's colours the other way round, or back. */
  setRtInverted?(id: string, on: boolean): void
  /** AYNI (owner, 2026-10-12): one step of a stream's transparency (1 more transparent). */
  stepRtOpacity?(id: string, dir: -1 | 1): void
  /** AYNI (owner, 2026-10-12): the dataset's eye, its ✕, and one step of its transparency. */
  setBaseHidden?(hidden: boolean): void
  closeBase?(): void
  stepBaseOpacity?(dir: -1 | 1): void
  setPalette(id: string): void
  setInverted(on: boolean): void
  resetView(): void
  /** AYNI: park the legend on the View page (true) or put it back beside the globe. */
  setLegendParked?(parked: boolean): void
  removePin(id: string): void
  /** AYNI: give a pin a name, or its own back with an empty one. */
  renamePin(id: string, name: string): void
  clearPins(): void
  markExtremes(): void
  /** AYNI (owner, 2026-10-18): the highest and lowest follow the data or stay; their track. */
  toggleFollow?(): void
  toggleTrack?(): void
  /** AYNI (owner, 2026-10-18): look for the extremes everywhere, on land only or at sea only. */
  setSurface?(mode: SurfaceMode): void
}

export interface VrSidePanelHandle {
  readonly mesh: THREE.Mesh
  /** Open a page, or close the panel with null. Opening the open page closes it. */
  toggle(kind: VrPanelKind): void
  close(): void
  current(): VrPanelKind | null
  isVisible(): boolean
  /** Poll the host and redraw on change. `now` in ms. */
  update(now: number): void
  hitTest(uv: { x: number; y: number }): VrPanelAction | null
  /** Carry out a press. */
  act(action: VrPanelAction): void
  /**
   * Thumbstick on the panel: scroll the page's list by `delta` canvas
   * pixels (positive = down), but only while the ray (`uv`) is over the
   * list — elsewhere on the panel the thumbstick does nothing.
   */
  scroll(delta: number, uv?: { x: number; y: number } | null): void
  /**
   * A press that became a drag: starting on the scroll bar it moves the
   * thumb, starting on the list it moves the list with the ray.
   */
  beginDrag(start: { x: number; y: number }): void
  dragTo(uv: { x: number; y: number }): void
  dispose(): void
}

/** Polling cadence while open; Browse's catalogue is polled less often. */
const POLL_MS = 200
const CATALOG_POLL_MS = 1000
/** While a list moves it is redrawn this often (a canvas upload per redraw). */
const SCROLL_REDRAW_MS = 33

export function createVrSidePanel(THREE_: typeof THREE, host: VrSidePanelHost): VrSidePanelHandle {
  const panel = createCanvasPanel<VrPanelAction>(THREE_, {
    widthM: SIDE_PANEL_WIDTH,
    heightM: SIDE_PANEL_HEIGHT,
    canvasW: SIDE_CANVAS_W,
    canvasH: SIDE_CANVAS_H,
  })
  panel.mesh.visible = false

  let kind: VrPanelKind | null = null
  let browse: VrBrowseView = initialBrowseView()
  let layersView: VrLayersView = initialLayersView()
  /** AYNI: the pin being renamed on the Data page, and what has been typed. */
  let pinEdit: VrPinEdit | null = null
  let datasets: VrDatasetEntry[] = []
  let lastCatalogPoll = -Infinity
  let lastPoll = -Infinity
  let drawn = ''
  /** The scrolling list the current page drew, if it has one. */
  let region: ScrollRegion | null = null
  /** A list moved and wants a redraw sooner than the poll. */
  let scrollDirty = false
  let lastScrollDraw = -Infinity
  /** A drag in progress: on the thumb (where it was grabbed) or on the list. */
  let drag:
    | { mode: 'thumb'; grabOffset: number }
    | { mode: 'list'; startY: number; startScroll: number }
    // AYNI: a playing stream's row, held by its grip: `from`/`to` are rows, top first.
    | { mode: 'rt'; id: string; startY: number; from: number; to: number }
    | null = null

  /** AYNI: the playing streams' ids as the Layers page lists them, top first. */
  function rtRowsTopFirst(): string[] {
    return [...(host.layers()?.rtPlaying ?? [])].reverse().map((rt) => rt.id)
  }
  /** Thumbnails by URL, decoded once per session. */
  const thumbs = new Map<string, HTMLImageElement>()

  function loadThumbs(entries: readonly VrDatasetEntry[]): void {
    for (const d of entries) {
      if (!d.thumbnailUrl || thumbs.has(d.thumbnailUrl)) continue
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.decoding = 'async'
      thumbs.set(d.thumbnailUrl, img)
      img.onload = () => { drawn = '' }
      img.src = d.thumbnailUrl
    }
  }

  function setKind(next: VrPanelKind | null): void {
    kind = next
    pinEdit = null
    panel.mesh.visible = next !== null
    drawn = ''
    region = null
    drag = null
    if (next === 'browse') lastCatalogPoll = -Infinity
  }

  function redraw(now: number): void {
    if (!kind) return
    if (kind === 'browse') {
      if (now - lastCatalogPoll >= CATALOG_POLL_MS) {
        lastCatalogPoll = now
        datasets = host.datasets()
      }
      const recent = browse.tab === 'recent' ? host.recentIds() : []
      const results = browseResults(datasets, browse, recent, host.search)
      const current = host.currentId()
      const sig = `b|${JSON.stringify(browse)}|${results.total}|${results.items[0]?.id ?? ''}|${current}|${datasets.length}`
      if (sig === drawn) return
      drawn = sig
      panel.draw((p) => {
        const out = drawBrowse(p, browse, datasets, results, current, thumbs)
        region = out.region
        // Thumbnails for the cards in view only: the whole catalogue is not fetched at once.
        loadThumbs(out.visible)
      })
      return
    }
    if (kind === 'layers') {
      const state = host.layers()
      const sig = `l|${JSON.stringify(state)}|${JSON.stringify(layersView)}`
      if (sig === drawn) return
      drawn = sig
      panel.draw((p) => { region = drawLayers(p, state, layersView) })
      return
    }
    region = null
    if (kind === 'view') {
      const state = host.view()
      const sig = `v|${JSON.stringify(state)}`
      if (sig === drawn) return
      drawn = sig
      panel.draw((p) => drawView(p, state))
      return
    }
    if (kind === 'data') {
      const state = host.pins()
      // A pin taken away while it was being renamed ends the renaming.
      if (pinEdit && !state?.pins.some((pin) => pin.id === pinEdit!.id)) pinEdit = null
      const sig = `d|${JSON.stringify(state)}|${JSON.stringify(pinEdit)}`
      if (sig === drawn) return
      drawn = sig
      panel.draw((p) => drawData(p, state, pinEdit))
      return
    }
    if (drawn === 'h') return
    drawn = 'h'
    panel.draw((p) => drawHelp(p))
  }

  function currentScroll(): number {
    return kind === 'browse' ? browse.scroll : kind === 'layers' ? layersView.scroll : 0
  }

  /** Scroll the current page's list to `value`, clamped; a change redraws soon. */
  function setScroll(value: number): void {
    if (!region) return
    const next = clampScroll(value, region)
    if (next === currentScroll()) return
    if (kind === 'browse') browse = { ...browse, scroll: next }
    else if (kind === 'layers') layersView = { ...layersView, scroll: next }
    else return
    scrollDirty = true
  }

  return {
    mesh: panel.mesh,
    toggle(next) {
      setKind(kind === next ? null : next)
    },
    close() {
      setKind(null)
    },
    current: () => kind,
    isVisible: () => kind !== null,
    update(now) {
      if (!kind) return
      if (scrollDirty && now - lastScrollDraw >= SCROLL_REDRAW_MS) {
        scrollDirty = false
        lastScrollDraw = now
        redraw(now)
        return
      }
      if (now - lastPoll < POLL_MS) return
      lastPoll = now
      redraw(now)
    },
    hitTest(uv) {
      return kind ? panel.hitTest(uv) : null
    },
    act(action) {
      switch (action.kind) {
        case 'close':
          setKind(null)
          return
        case 'browse-tab':
        case 'browse-kind':
        case 'browse-theme':
        case 'browse-search':
        case 'key':
          browse = reduceBrowse(browse, action)
          drawn = ''
          return
        case 'browse-select': {
          if (action.locked) {
            const entry = datasets.find((d) => d.id === action.id)
            host.lockedChosen(action.locked, entry?.title ?? '')
            return
          }
          host.loadDataset(action.id)
          browse = { ...browse, keyboard: false }
          setKind(null)
          return
        }
        case 'layer-section':
          layersView = { section: layersView.section === action.section ? null : action.section, scroll: 0, editing: null }
          drawn = ''
          return
        case 'layer-overlay': host.setOverlay(action.id); drawn = ''; return
        case 'sat':
        case 'sat-show':
        case 'sat-colour':
        case 'sat-altitude':
        case 'sat-speed':
          host.satellite?.(action)
          drawn = ''
          return
        case 'layer-tint': host.setTint(action.id, action.tint); drawn = ''; return
        case 'layer-rt': host.toggleRt(action.id); drawn = ''; return
        case 'layer-rt-hidden': host.setRtHidden(action.id, action.hidden); drawn = ''; return
        // A press on the grip alone does nothing; dragging from it moves the stream (beginDrag).
        case 'layer-rt-grip': return
        case 'layer-rt-colours': layersView = { ...layersView, editing: action.id }; drawn = ''; return
        case 'rt-palette': host.setRtPalette(action.id, action.palette); drawn = ''; return
        case 'rt-limit': host.stepRtLimit(action.id, action.end, action.dir); drawn = ''; return
        case 'rt-invert': host.setRtInverted?.(action.id, action.on); drawn = ''; return
        case 'rt-opacity': host.stepRtOpacity?.(action.id, action.dir); drawn = ''; return
        case 'base-hidden': host.setBaseHidden?.(action.hidden); drawn = ''; return
        case 'base-close': host.closeBase?.(); drawn = ''; return
        case 'base-colours': setKind('view'); return
        case 'base-opacity': host.stepBaseOpacity?.(action.dir); drawn = ''; return
        case 'palette': host.setPalette(action.id); drawn = ''; return
        case 'invert': host.setInverted(!(host.view()?.inverted ?? false)); drawn = ''; return
        case 'references': setReferenceLinesVisible(!referenceLinesVisible()); drawn = ''; return
        case 'reset-view': host.resetView(); drawn = ''; return
        case 'legend-park': host.setLegendParked?.(action.parked); drawn = ''; return
        case 'pin-remove': host.removePin(action.id); drawn = ''; return
        case 'pin-rename':
        case 'pin-key': {
          const step = reducePinEdit(pinEdit, action, host.pins())
          pinEdit = step.edit
          if (step.commit) host.renamePin(step.commit.id, step.commit.name)
          drawn = ''
          return
        }
        case 'pins-clear': host.clearPins(); drawn = ''; return
        case 'pins-extremes': host.markExtremes(); drawn = ''; return
        case 'pins-follow': host.toggleFollow?.(); drawn = ''; return
        case 'pins-track': host.toggleTrack?.(); drawn = ''; return
        case 'pins-surface': host.setSurface?.(action.mode); drawn = ''; return
      }
    },
    scroll(delta, uv) {
      if (!region) return
      if (uv) {
        const c = panel.toCanvas(uv)
        if (!insideRegion(region, c.x, c.y)) return
      }
      setScroll(currentScroll() + delta)
    },
    beginDrag(start) {
      drag = null
      const c = panel.toCanvas(start)
      // AYNI: a drag from a playing stream's grip moves that stream up or down.
      const hit = kind === 'layers' ? panel.hitTest(start) : null
      if (hit?.kind === 'layer-rt-grip') {
        const from = rtRowsTopFirst().indexOf(hit.id)
        if (from >= 0) drag = { mode: 'rt', id: hit.id, startY: c.y, from, to: from }
        return
      }
      if (!region) return
      const thumb = scrollbarThumb(region, currentScroll())
      if (thumb && onScrollbar(region, c.x, c.y)) {
        // Grabbed on the thumb: keep the grab point under the ray. On the
        // track: the thumb jumps to put its middle there.
        const onThumb = c.y >= thumb.y && c.y <= thumb.y + thumb.h
        drag = { mode: 'thumb', grabOffset: onThumb ? c.y - thumb.y : thumb.h / 2 }
        if (!onThumb) setScroll(scrollForThumb(region, c.y - drag.grabOffset))
      } else if (insideRegion(region, c.x, c.y)) {
        drag = { mode: 'list', startY: c.y, startScroll: currentScroll() }
      }
    },
    dragTo(uv) {
      if (drag?.mode === 'rt') {
        const rows = rtRowsTopFirst()
        const to = Math.max(0, Math.min(rows.length - 1, Math.round(drag.from + (panel.toCanvas(uv).y - drag.startY) / LAYER_ROW_H)))
        if (to === drag.to || !rows.includes(drag.id)) return
        drag.to = to
        // The stack is bottom first; the rows are top first.
        host.moveRt(drag.id, rows.length - 1 - to)
        drawn = ''
        return
      }
      if (!drag || !region) return
      const y = panel.toCanvas(uv).y
      if (drag.mode === 'thumb') setScroll(scrollForThumb(region, y - drag.grabOffset))
      else setScroll(drag.startScroll + (drag.startY - y))
    },
    dispose() {
      panel.dispose()
      thumbs.clear()
    },
  }
}
